
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import DataTable from "./DataTable";
import { format } from "date-fns";

// Create a lightweight logging utility that only logs in development
const isDevelopment = import.meta.env.DEV;
const logger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },
  error: (...args: any[]) => {
    if (isDevelopment) {
      console.error(...args);
    }
  }
};

const BlogAdmin = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      logger.log("Fetched blog posts:", data);
      setBlogPosts(data || []);
    } catch (error: any) {
      toast({
        title: "Failed to load blog posts",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (post: any) => {
    if (!confirm("Are you sure you want to delete this blog post?")) return;

    try {
      const { error } = await supabase
        .from("blog_posts")
        .delete()
        .match({ id: post.id });

      if (error) throw error;

      toast({
        title: "Blog post deleted",
        description: "The blog post has been successfully deleted.",
      });

      await fetchBlogPosts();
    } catch (error: any) {
      toast({
        title: "Failed to delete blog post",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const columns = [
    {
      key: "title",
      label: "Title",
    },
    {
      key: "category",
      label: "Category",
      format: (value: string) => (
        <Badge variant="outline">{value}</Badge>
      ),
    },
    {
      key: "published",
      label: "Status",
      format: (value: boolean) => (
        <Badge variant={value ? "default" : "secondary"}>
          {value ? "Published" : "Draft"}
        </Badge>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      format: (value: string) => format(new Date(value), "MMM d, yyyy"),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Blog Posts</h2>
        <Button
          size="sm"
          onClick={() => navigate("/blog/create")}
          className="flex items-center gap-2"
        >
          <PlusCircle className="h-4 w-4" /> Add Blog Post
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-pulse">Loading blog posts...</div>
        </div>
      ) : (
        <DataTable
          data={blogPosts}
          columns={columns}
          onView={(post) => window.open(`/blog/${post.id}`, "_blank")}
          onEdit={(post) => navigate(`/blog/edit/${post.id}`)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default BlogAdmin;
